'use client';

import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';
import { SIcon, type SIconName } from '../SIcon';
import {
  buttonPreset,
  BUTTON_SIZE_CONFIG,
  BUTTON_FOCUS_RING,
  BUTTON_DISABLED_BG,
  BUTTON_DISABLED_BORDER,
  BUTTON_DISABLED_TEXT,
  BUTTON_DISABLED_ICON,
  type SButtonColor,
  type SButtonSize,
} from './button.config';

export interface SButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'color' | 'children'
> {
  /** 색상 — sd-button preset의 색 계열 */
  color?: SButtonColor;
  /** 아웃라인(테두리) 스타일. primary·neutral·danger 만 지원 (secondary 는 무시) */
  outline?: boolean;
  /** 크기 */
  size?: SButtonSize;
  /** 레이블 왼쪽 아이콘 */
  icon?: SIconName;
  /** 레이블 오른쪽 아이콘 */
  rightIcon?: SIconName;
  /** 버튼 텍스트 (문자열만 — 아이콘은 icon/rightIcon 사용) */
  label?: string;
}

/**
 * SButton — sd-button 포팅.
 * color(4) × outline × 4개 사이즈, 아이콘(icon/rightIcon), 컴포넌트 토큰(--cmp-button-*) 기반.
 * 텍스트는 label(문자열)로만 받는다. 이벤트: sdClick → 네이티브 onClick.
 */
export const SButton = forwardRef<HTMLButtonElement, SButtonProps>(function SButton(
  {
    color = 'primary',
    outline = false,
    size = 'sm',
    icon,
    rightIcon,
    label,
    className,
    style,
    type = 'button',
    disabled = false,
    'aria-label': ariaLabel,
    ...props
  },
  ref,
) {
  const preset = buttonPreset(color, outline);
  const s = BUTTON_SIZE_CONFIG[size];
  const hasLabel = label != null && label !== '';
  const iconOnly = !hasLabel && Boolean(icon) !== Boolean(rightIcon);

  if (color === 'secondary' && outline && typeof console !== 'undefined') {
    console.warn(
      '[SButton] color="secondary" 는 outline 을 지원하지 않습니다. solid 로 렌더됩니다.',
    );
  }
  if (iconOnly && !ariaLabel && typeof console !== 'undefined') {
    console.warn(`[SButton] 아이콘 전용 버튼은 aria-label 이 필요합니다. (color="${color}")`);
  }

  const iconColor = disabled ? BUTTON_DISABLED_ICON : preset.icon;
  const iconNode = icon ? <SIcon name={icon} size={s.iconSize} color={iconColor} /> : null;
  const rightIconNode = rightIcon ? (
    <SIcon name={rightIcon} size={s.iconSize} color={iconColor} />
  ) : null;

  const cssVars = {
    '--btn-bg': disabled ? BUTTON_DISABLED_BG : preset.bg,
    '--btn-bg-hover': preset.bgHover,
    '--btn-border': disabled ? BUTTON_DISABLED_BORDER : preset.border,
    '--btn-ring': BUTTON_FOCUS_RING,
    'minHeight': s.height,
    'minWidth': iconOnly ? s.height : hasLabel ? s.minWidth : undefined,
    'padding': iconOnly ? 0 : `0 ${s.paddingX}`,
    'borderRadius': s.radius,
    'gap': s.gap,
    'fontSize': s.fontSize,
    'fontWeight': s.fontWeight,
    'color': disabled ? BUTTON_DISABLED_TEXT : preset.text,
    ...style,
  } as CSSProperties;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap border border-solid leading-none transition-colors',
        'border-[color:var(--btn-border)] bg-[var(--btn-bg)]',
        'outline-none focus-visible:shadow-[0_0_0_2px_var(--btn-ring)]',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--btn-bg-hover)]',
        className,
      )}
      style={cssVars}
      {...props}
    >
      {iconNode}
      {hasLabel && <span className="inline-flex items-center justify-center">{label}</span>}
      {rightIconNode}
    </button>
  );
});
